/**
 * promo-badges.js
 * ──────────────────────────────────────────────────────────────────────────
 * Reads window.productBadges (array built server-side by sections/promo-badges.liquid)
 * and injects badge HTML into every .product-promo-badges[data-product-tags]
 * placeholder on the page — including cards rendered asynchronously by
 * SearchSpring on collection pages.
 *
 * Badge HTML rendered per matched promo:
 *   <a href="…" class="promo-badge js-promo-badge-trigger">
 *     [<img src="…">] <span>Grid Item Badge text</span>
 *   </a>
 * Multiple badges are joined with an "or" separator (inline, not stacked).
 *
 * Badge field priority for grid cards: badge.item → badge.title → badge.tag
 *
 * Usage: loaded via <script src="promo-badges.js" defer> inside
 *        sections/promo-badges.liquid so it is only present when the section
 *        is active.
 */

(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────────────────── */

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Build the inner HTML for a single badge anchor/span.
   * Uses badge.item (Grid Item Badge) as label, falling back to title then tag.
   */
  function buildBadgeHTML(badge) {
    // option badges store text in badge.label; others use item/title/tag
    var label = badge.label || badge.item || badge.title || badge.tag || '';
    var color = badge.colour || badge.color || '#006F29';
    var inner = '';

    // Resolve icon to show before text
    var iconSrc = '';
    if (badge.type === 'icon' || badge.type === 'graphic') {
      // Image Badge Map / Hover Image Map: icon field is already a full CDN URL
      iconSrc = badge.icon || '';
    } else {
      // text / option badges: always show promo-tag.svg
      iconSrc = window.promoTagIconUrl || '';
    }

    if (iconSrc) {
      inner += '<img src="' + esc(iconSrc) + '" alt="" aria-hidden="true" style="width:14px !important;height:14px !important;object-fit:contain !important;flex-shrink:0 !important;">';
    }
    inner += '<span>' + esc(label) + '</span>';

    if (badge.url) {
      var handle = badge.url.split('/').filter(Boolean).pop() || '';
      handle = handle.split('?')[0].split('#')[0];
      return (
        '<a href="' + esc(badge.url) + '"' +
        ' class="promo-badge js-promo-badge-trigger"' +
        ' data-promo-handle="' + esc(handle) + '"' +
        ' data-promo-title="' + esc(label) + '"' +
        ' style="color:' + esc(color) + ';display:inline-flex;align-items:center;gap:4px;font-size:11px;text-decoration:none;white-space:nowrap;">' +
        inner +
        '</a>'
      );
    }

    // No URL: still render as a clickable anchor pointing to the collection derived
    // from the badge tag. In cart/YMAL context product-promo-modal.js will intercept
    // the click and open the multi-buy popup instead of navigating.
    var tagHandle = (badge.tag || label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    var collectionUrl = root + 'collections/' + tagHandle;
    return (
      '<a href="' + esc(collectionUrl) + '"' +
      ' class="promo-badge js-promo-badge-trigger"' +
      ' data-promo-handle="' + esc(tagHandle) + '"' +
      ' data-promo-title="' + esc(label) + '"' +
      ' style="color:' + esc(color) + ';display:inline-flex;align-items:center;gap:4px;font-size:11px;text-decoration:none;white-space:nowrap;">' +
      inner +
      '</a>'
    );
  }

  /* ── Core injection ──────────────────────────────────────────────────── */

  function injectBadges() {
    var badges = window.productBadges;
    if (!badges || !badges.length) return;

    var placeholders = document.querySelectorAll(
      '.product-promo-badges[data-product-tags]:not([data-badges-injected])'
    );

    placeholders.forEach(function (el) {
      el.setAttribute('data-badges-injected', '');

      var rawTags = el.getAttribute('data-product-tags') || '';
      var tags = rawTags.split('||').map(function (t) { return t.trim(); }).filter(Boolean);

      var matched = badges.filter(function (b) {
         if (!b.tag) return false;
        var cleanBTag = b.tag.trim().toLowerCase();
        return tags.some(function (t) {
          return t.toLowerCase() === cleanBTag;
        });
      });

      if (!matched.length) return;

      var html = '';
      matched.forEach(function (badge, i) {
        if (i > 0) {
          html += '<span class="promo-badge-separator" style="font-size:14px;color:#006F29;"> or </span>';
        }
        html += buildBadgeHTML(badge);
      });

      el.innerHTML = html;
      // Always use flex so inline display works for both block and snippet versions
      el.style.display = 'flex';
      el.style.flexWrap = 'nowrap';
      el.style.alignItems = 'center';
      el.style.gap = '4px';
      el.style.overflow = 'visible';
    });
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectBadges);
  } else {
    injectBadges();
  }

  var observer = new MutationObserver(function (mutations) {
    var runInjection = false;

    mutations.forEach(function (m) {
      if (m.type === 'childList') {
        var hasNewNode = Array.from(m.addedNodes).some(function (node) {
          if (node.nodeType !== 1) return false;
          return (
            (node.classList && node.classList.contains('product-promo-badges') && node.hasAttribute('data-product-tags')) ||
            (node.querySelector && node.querySelector('.product-promo-badges[data-product-tags]'))
          );
        });
        if (hasNewNode) runInjection = true;
      } else if (m.type === 'attributes' && m.attributeName === 'data-product-tags') {
        var el = m.target;
        if (el.classList && el.classList.contains('product-promo-badges')) {
          el.removeAttribute('data-badges-injected');
          runInjection = true;
        }
      }
    });

    if (runInjection) injectBadges();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-product-tags']
  });

  document.addEventListener('cart:update', function () {
    setTimeout(injectBadges, 50);
    setTimeout(injectBadges, 300);
  });

  setInterval(injectBadges, 1000);

  window.injectPromoBadges = injectBadges;
})();
